import React, { useRef, useEffect } from 'react';
import { ScrollArea } from './ui/scroll-area';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { Bot, MessageSquare } from 'lucide-react';

export default function ModernChat({ 
  messages = [], 
  onSendMessage, 
  isProcessing = false,
  disabled = false,
  placeholder = "Ask me anything about your project...",
  emptyStateTitle = "Start a conversation",
  emptyStateDescription = "Ask questions, generate content, or get help with your project.",
  streamingMessage = null
}) {
  const scrollRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  const allMessages = streamingMessage 
    ? [...messages, { role: 'assistant', content: streamingMessage, isStreaming: true }]
    : messages;

  return (
    <div className="flex flex-col h-full bg-[var(--surface-bg)]" data-testid="modern-chat">
      {/* Messages Area */}
      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="min-h-full">
          {allMessages.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] px-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center mb-4">
                <Bot className="w-8 h-8 text-[var(--accent-indigo)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{emptyStateTitle}</h3>
              <p className="text-sm text-[var(--text-secondary)] text-center max-w-md mb-6">{emptyStateDescription}</p>
              
              {/* Suggestion chips */}
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {[
                  "Generate requirements for my project",
                  "Help me design the architecture",
                  "Create test cases",
                  "Explain best practices"
                ].map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => onSendMessage(suggestion)}
                    disabled={disabled || isProcessing}
                    className="px-4 py-2 text-sm rounded-full border border-[var(--surface-border)] bg-[var(--surface-card)] hover:bg-[var(--accent-indigo)]/10 hover:border-[var(--accent-indigo)]/30 hover:text-[var(--accent-indigo)] transition-all disabled:opacity-50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Messages List */
            <div className="divide-y divide-[var(--surface-border)]/50">
              {allMessages.map((msg, idx) => (
                <ChatMessage
                  key={idx}
                  message={msg}
                  isUser={msg.role === 'user'}
                  isStreaming={msg.isStreaming}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <ChatInput
        onSend={onSendMessage}
        disabled={disabled}
        isProcessing={isProcessing}
        placeholder={placeholder}
      />
    </div>
  );
}
